import React, { useEffect, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { fmChmod } from "../../redux/actionCreator";

/**
 * cPanel-style permissions editor. Mode is octal — three triplets for
 * owner/group/other, each combining read/write/execute. Setuid/setgid/sticky
 * bits are exposed in the "Special" row.
 */
const TRIPLET = [
  { label: "Owner", shift: 6 },
  { label: "Group", shift: 3 },
  { label: "Other", shift: 0 },
];
const SPECIAL = [
  { label: "Setuid", bit: 0o4000 },
  { label: "Setgid", bit: 0o2000 },
  { label: "Sticky", bit: 0o1000 },
];

function bitsForShift(mode, shift) {
  return {
    read: !!(mode & (0o4 << shift)),
    write: !!(mode & (0o2 << shift)),
    exec: !!(mode & (0o1 << shift)),
  };
}

function toMode(state) {
  let m = 0;
  for (const t of TRIPLET) {
    if (state[t.label].read) m |= 0o4 << t.shift;
    if (state[t.label].write) m |= 0o2 << t.shift;
    if (state[t.label].exec) m |= 0o1 << t.shift;
  }
  for (const s of SPECIAL) if (state.special[s.label]) m |= s.bit;
  return m;
}

export default function PermissionsModal({
  projectId,
  serverId,
  filePath,
  fileName,
  initialMode,
  onClose,
  onApplied,
}) {
  const dispatch = useDispatch();
  const filePerms = (initialMode & 0o777) | (initialMode & 0o7000);
  const [state, setState] = useState(() => ({
    Owner: bitsForShift(filePerms, 6),
    Group: bitsForShift(filePerms, 3),
    Other: bitsForShift(filePerms, 0),
    special: SPECIAL.reduce((acc, s) => {
      acc[s.label] = !!(filePerms & s.bit);
      return acc;
    }, {}),
  }));
  const [octalInput, setOctalInput] = useState(
    (filePerms & 0o7777).toString(8).padStart(4, "0")
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setOctalInput(toMode(state).toString(8).padStart(4, "0"));
  }, [state]);

  const flip = (triplet, key) =>
    setState((s) => ({ ...s, [triplet]: { ...s[triplet], [key]: !s[triplet][key] } }));
  const flipSpecial = (label) =>
    setState((s) => ({ ...s, special: { ...s.special, [label]: !s.special[label] } }));

  const onOctalChange = (raw) => {
    setOctalInput(raw);
    if (!/^[0-7]{3,4}$/.test(raw)) return;
    const m = parseInt(raw, 8);
    setState({
      Owner: bitsForShift(m, 6),
      Group: bitsForShift(m, 3),
      Other: bitsForShift(m, 0),
      special: SPECIAL.reduce((acc, s) => {
        acc[s.label] = !!(m & s.bit);
        return acc;
      }, {}),
    });
  };

  const apply = async () => {
    if (!/^[0-7]{3,4}$/.test(octalInput)) {
      alert("Mode must be octal (e.g. 0755).");
      return;
    }
    setSaving(true);
    const res = await dispatch(fmChmod(projectId, serverId, filePath, octalInput));
    setSaving(false);
    if (res?.status) {
      onApplied && onApplied();
      onClose && onClose();
    } else {
      alert(res?.message || "Failed to chmod");
    }
  };

  return (
    <Modal show onHide={onClose} centered>
      <Modal.Header closeButton>
        <h4 className="m-0">
          Permissions — <code>{fileName}</code>
        </h4>
      </Modal.Header>
      <Modal.Body>
        <table className="table table-sm align-middle mb-3">
          <thead>
            <tr>
              <th></th>
              <th className="text-center">Read</th>
              <th className="text-center">Write</th>
              <th className="text-center">Execute</th>
            </tr>
          </thead>
          <tbody>
            {TRIPLET.map((t) => (
              <tr key={t.label}>
                <td>
                  <strong>{t.label}</strong>
                </td>
                {["read", "write", "exec"].map((k) => (
                  <td className="text-center" key={k}>
                    <Form.Check
                      type="checkbox"
                      checked={state[t.label][k]}
                      onChange={() => flip(t.label, k)}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>

        <details className="mb-3">
          <summary className="text-muted">Special bits</summary>
          <div className="d-flex gap-3 mt-2">
            {SPECIAL.map((s) => (
              <Form.Check
                key={s.label}
                type="checkbox"
                label={s.label}
                checked={!!state.special[s.label]}
                onChange={() => flipSpecial(s.label)}
              />
            ))}
          </div>
        </details>

        <Form.Group>
          <Form.Label>Octal mode</Form.Label>
          <Form.Control
            value={octalInput}
            onChange={(e) => onOctalChange(e.target.value)}
            maxLength={4}
            style={{ fontFamily: "ui-monospace, monospace", letterSpacing: "0.1em" }}
          />
          <small className="text-muted">
            e.g. <code>0755</code> for executables, <code>0644</code> for regular files,
            <code>0700</code> for private folders.
          </small>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" disabled={saving} onClick={apply}>
          {saving ? "Applying…" : "Apply"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
